/**
 * Danish message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal du (locked in l10n/locales.md).
 *
 * Platform terms follow Danish UI conventions (Tema for theme, samtale
 * for conversation, parre for pair, afvise for dismiss, modtager for a
 * receiving surface); "Glance" is the untranslated feature brand name
 * (glossary D1) and "klip" is a locked loanword (D2).
 *
 * Plural notes: Danish is a {one, other} CLDR locale, and its count
 * nouns mostly inflect (enhed → enheder, resultat → resultater) while a
 * few stay invariant (tegn → tegn, opkald → opkald), so every
 * count-bearing function selects through `selectPlural("da", …)`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Forbundet",
    statusReconnecting: "Genopretter forbindelse…",
    statusDisconnected: "Afbrudt",
    openSettings: "Åbn indstillinger",
    pairedFallbackDevice: "din telefon",
    publicModeToggleEnable: "Skjul notifikationsindhold",
    publicModeToggleDisable: "Vis notifikationsindhold",
  },

  notifications: {
    replyAriaLabel: "Svar",
    replyPlaceholder: "Svar…",
    replySend: "Send",
    sent: "Sendt",
    failedToSend: "Kunne ikke sende",
    unread: "Ulæst",
    dismiss: "Afvis",
    notificationImageAlt: "Notifikationsbillede",
  },

  conversations: {
    replyAriaLabel: "Svar",
    replyPlaceholder: "Svar…",
    replySend: "Send",
    sent: "Sendt",
    failedToSend: "Kunne ikke sende",
    dismiss: "Afvis samtale",
    messagesListLabel: "Beskeder i denne samtale",
    deviceOwner: "Dig",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Ingen ulæste beskeder"
        : `${count} ${selectPlural("da", count, {
            one: "ulæst besked",
            other: "ulæste beskeder",
          })}`,
  },

  list: {
    today: "I dag",
    yesterday: "I går",
    searchNotifications: "Søg i notifikationer",
    clearSearch: "Ryd søgning",
    clearAll: "Ryd alle",
    markAllRead: "Markér alle som læst",
    groupCheckboxLabel: "Gruppér",
    noNotificationsTitle: "Endnu ingen notifikationer",
    noNotificationsDesc: "Nye beskeder, der spejles fra din telefon, vises her.",
    noMatchesTitle: "Ingen resultater",
    notificationsList: "Notifikationer",
    privacyAppList: "Notifikationer efter app",
    clear: "Ryd",
    clearMatches: (count: number): string =>
      `Ryd ${count} ${selectPlural("da", count, { one: "resultat", other: "resultater" })}`,
    noMatchesDesc: (query: string): string => `Ingen notifikationer matcher ”${query}”.`,
    appNotificationsGroup: (appName: string): string => `Notifikationer fra ${appName}`,
    dismissAllFrom: (appName: string): string => `Afvis alle fra ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("da", count, { one: "besked", other: "beskeder" })
          : kind === "calls"
            ? selectPlural("da", count, { one: "opkald", other: "opkald" })
            : selectPlural("da", count, { one: "notifikation", other: "notifikationer" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "afvis",
    kbdNavigate: "naviger",
    kbdOrSeparator: "eller",
  },

  settings: {
    back: "Tilbage til notifikationer",
    settings: "Indstillinger",
    appearance: "Udseende",
    theme: "Tema",
    themeAuto: "Automatisk",
    themeLight: "Lyst",
    themeDark: "Mørkt",
    language: "Sprog",
    languageSystemDefault: "Systemstandard",
    notificationsSection: "Notifikationer",
    osNotifications: "Systemnotifikationer",
    callNotifications: "Opkaldsnotifikationer",
    privacySection: "Privatliv",
    analyticsToggle: "Anonym brugsstatistik",
    analyticsToggleDescription: "Kun antal og milepæle — aldrig dine notifikationer.",
    connection: "Forbindelse",
    status: "Status",
    statusConnected: "Forbundet",
    statusReconnecting: "Genopretter forbindelse…",
    statusDisconnected: "Afbrudt",
    disconnectFromPhone: "Afbryd forbindelsen til telefonen",
    disconnectConfirm: "Er du sikker? Dette ophæver parringen af din enhed.",
    yesDisconnect: "Ja, afbryd",
    cancel: "Annuller",
    aboutAndSupport: "Om & support",
    version: "Version",
    encryption: "Kryptering",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Brug for hjælp? Skriv til __EMAIL__",
    disconnectFailed: "Kunne ikke afbryde forbindelsen — prøv igen",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Forbindelsen blev afbrudt, men lokal oprydning mislykkedes: ${errorMsg}. Genindlæs siden.`,
    disconnectLocalCleanupFailed:
      "Forbindelsen blev afbrudt, men lokal oprydning mislykkedes. Genindlæs siden.",
  },

  pairing: {
    enterCodeInstruction: "Indtast denne kode i Reflecto-appen",
    pairedSuccessfully: "Parring gennemført!",
    pairingFailed: "Parring mislykkedes",
    requestingPairingCode: "Anmoder om parringskode…",
    codeExpired: "Kode udløbet",
    tryAgain: "Prøv igen",
    failedToStartPairing: "Kunne ikke starte parring",
    needAppText: "Mangler du Android-appen? Skriv til __EMAIL__ for at få adgang.",
  },

  dnd: {
    title: "Forstyr ikke",
    description: "Sæt notifikationer på denne enhed på pause den næste time.",
    snooze1h: "Sæt på pause i 1 time",
    snoozeActiveUntil: (formattedTime: string): string => `Aktiv indtil ${formattedTime}`,
    cancelSnooze: "Annuller",
  },

  connectors: {
    title: "Forbundne tjenester",
    emptyBody:
      "Endnu ingen forbundne tjenester. Forbind ChatGPT eller Claude fra deres connector-indstillinger, og godkend derefter koden på din telefon.",
    scopeSend: "Kan sende notifikationer",
    added: (date: string): string => `Tilføjet ${date}`,
    lastUsed: (relative: string): string => `Sidst brugt ${relative}`,
    neverUsed: "Aldrig brugt",
    revoke: "Afbryd",
    revokeConfirm: (label: string): string => `Afbryd ${label}? Adgangen ophører øjeblikkeligt.`,
    revokeAction: "Afbryd",
    revokeConfirmLabel: (label: string): string => `Afbryd ${label}`,
    cancel: "Annuller",
    loadError: "Kunne ikke indlæse forbundne tjenester. Prøv igen.",
    revokeError: "Kunne ikke afbryde tjenesten. Prøv igen.",
    retry: "Prøv igen",
  },

  feedback: {
    title: "Send feedback",
    close: "Luk",
    messageLabel: "Din besked",
    messagePlaceholder:
      "Fandt du en fejl, har du en idé, eller føles noget forkert? Fortæl os det.",
    categoryLabel: "Kategori",
    categoryBug: "Fejl",
    categoryIdea: "Idé",
    categoryOther: "Andet",
    emailLabel: "E-mail (valgfrit, kun hvis du vil have et svar)",
    emailPlaceholder: "dig@eksempel.dk",
    emailHelp: "Vi tilføjer dig ikke til nogen liste.",
    disclosure:
      "Din besked sendes til Reflectos server og registreres i vores interne issue-tracker. Vi vedhæfter din app-version og et enheds-ID, der tildeles under parring, så vi kan sammenholde rapporter. Vi ser ikke dine notifikationer, kontakter eller andre personlige data. E-mail er valgfrit — angiv den kun, hvis du vil have et svar. Vi tilføjer dig ikke til nogen liste.",
    submit: "Send feedback",
    sending: "Sender…",
    success: "Tak — læses af et menneske, som regel inden for 24 timer",
    error: "Kunne ikke sende. Prøv igen.",
    done: "Færdig",
    sendFeedback: "Send feedback",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Luk",
    aiSummaryTag: "AI-resumé",
    errorAi: "Kunne ikke lave et resumé lige nu.",
    retryCta: "Prøv igen",
    downloadHeading: "Få AI-resuméer",
    downloadBody:
      "Bliv opdateret på det, du gik glip af, med et AI-resumé på selve enheden. Kører udelukkende på din enhed.",
    downloadCta: "Hent model",
    downloadPrivacyNote: "En engangshentning. Serveren ser aldrig dine notifikationer.",
    downloadingHeading: "Henter model…",
    caughtUp: "Du er helt opdateret — der er intet, der kræver din opmærksomhed lige nu.",
    needsYou: "Kræver opmærksomhed",
    quiet: "Øvrige",
    scanning: (pending: number): string => `Opsummerer ${pending}…`,
    jump: "Åbn notifikation",
    affordanceReply: "Svar",
    affordanceOpen: "Åbn",
    affordanceCopyOtp: "Kopiér kode",
    buttonLabel: "Glance",
  },

  update: {
    title: "Opdatering tilgængelig",
    description: "En ny version af Reflecto er klar. Genindlæs for at opdatere.",
    reload: "Genindlæs",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Vi kaldte denne enhed ”${name}”.`,
    cardLooksGood: "Ser godt ud",
    cardEdit: "Rediger",
    cardAriaLabel: "Bekræft enhedsnavn",
    renameTitle: "Omdøb denne enhed",
    renameLabel: "Enhedsnavn",
    renameSave: "Gem",
    renameCancel: "Annuller",
    renameFailed: "Kunne ikke omdøbe. Prøv igen.",
  },

  collision: {
    title: (name: string): string => `Du har allerede en ”${name}”`,
    subtitle: "Hvad skal vi kalde denne?",
    chipWork: (name: string): string => `Arbejde ${name}`,
    chipHome: (name: string): string => `Hjemme ${name}`,
    chipPersonal: (name: string): string => `Privat ${name}`,
    customLabel: "Brugerdefineret navn",
    save: "Gem",
    useAnyway: "Brug alligevel det oprindelige navn",
    saveFailed: "Kunne ikke gemme det nye navn. Prøv igen.",
    ariaLabel: "Løs dobbelt enhedsnavn",
  },

  clipboard: {
    title: "Udklipsholder",
    empty: "Klip fra dine andre enheder vises her. Tryk for at kopiere.",
    from: (label: string): string => `fra ${label}`,
    copy: "Kopiér til udklipsholder",
    copied: "Kopieret",
    reveal: "Vis",
    hide: "Skjul",
    maskedHint: "Indhold skjult — tryk på Vis for at se det",
    sensitiveChip: "Følsom",
    cloudChip: "fra dit API-token",
    sendPlaceholder: "Indsæt tekst, der skal sendes…",
    sendCta: "Send",
    sendSensitiveToggle: "Markér som følsom",
    sendSuccess: (n: number): string =>
      `Sendt til ${n} ${selectPlural("da", n, { one: "enhed", other: "enheder" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Nåede ${n}/${total} ${selectPlural("da", total, { one: "enhed", other: "enheder" })}.`,
    sendNoDevices: "Ingen parrede enheder at sende til.",
    sendFailed: "Kunne ikke sende. Prøv igen.",
    sendEmpty: "Intet at sende.",
    dockExpand: "Vis udklipsholderhistorik",
    dockCollapse: "Fold sammen",
    dockSendTitle: "Send til dine enheder",
    dockFromClipboard: (chars: number): string =>
      `Fra udklipsholder · ${chars} ${selectPlural("da", chars, {
        one: "tegn",
        other: "tegn",
      })}`,
    dockSend: (n: number): string => `Send til ${n}`,
    dockCancel: "Annuller",
    dockRecentHeading: "Seneste",
    dockEmptyClipboard: "Din udklipsholder er tom.",
    shareTitle: "Send til dine enheder",
    shareReview: "Gennemse det, du sender. Det går til dine parrede enheder, end-to-end-krypteret.",
    shareSend: "Send",
    shareCancel: "Annuller",
    shareNotPaired: "Par en enhed først for at sende delt tekst.",
    shareOpenApp: "Åbn Reflecto",
  },
  install: {
    title: "Installer Reflecto",
    body: "Føj til din startskærm for hurtigere adgang.",
    cta: "Installer",
    dismissAria: "Afvis installationsprompt",
  },
  common: {
    loading: "Indlæser",
  },
  errors: {
    notPaired: "Ikke parret",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} på ${os}`,
    webFallback: "Webbrowser",
  },

  palette: {
    placeholder: "Skriv en kommando…",
    noResults: "Ingen matchende kommandoer",
    ariaLabel: "Kommandopalet",
    clearAll: "Ryd alle notifikationer",
    glance: "Glance-resumé",
    cancelSnooze: "Annuller pause",
    copyClip: "Kopiér seneste modtagne klip",
  },
} satisfies MessagesShape;
